import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Observable } from "rxjs";
import { FormRendererContext, ObservableValue } from "./Context";
import { FormContainerWithTranslations } from "./FormContainer";
import {
  convertIn as convertInModel,
  convertOut as convertOutModel,
  validateModel,
} from "./FormModel";
import { StateDispatcherRenderProps } from "./StateDispatcher";
import { ConversionJobs, WithFormProps, _FormProps } from "./types";

export type FormRendererContextProps = {
  dirty: boolean;
  onChange: (value: any, statePath: string, validation: any) => void;
  resetValidation: (paths?: ObservableValue["paths"]) => void;
  submit: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  valueChangeObs: Observable<ObservableValue>;
};

export type WithChildren = {
  children: (props: FormRendererContextProps) => ReactNode;
};

type WithModelDefinition = {
  modelDefinition: ConversionJobs;
};

type FormRenderPropsProps<Props> = Props & WithChildren & WithFormProps;

type SimpleFormRenderPropsProps<Props> = Props &
  WithChildren &
  WithFormProps &
  WithModelDefinition;

export function SimpleForm({
  modelDefinition,
  resetAfterSubmit,
}: WithModelDefinition & Pick<WithFormProps, "resetAfterSubmit">) {
  return Form({
    convertIn: convertInModel(modelDefinition),
    convertOut: convertOutModel(modelDefinition),
    modelDefinition,
    resetAfterSubmit,
    validate: validateModel(modelDefinition),
  });
}

export const handleInputFocusOnSubmit = (elements: HTMLInputElement[]) => {
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const isAriaInvalid = element.getAttribute("aria-invalid") === "true";

    if (!element.checkValidity() || isAriaInvalid) {
      // If the invalid element that will receive validation focus is currently in focus
      // then it won't be reanounced and hence the error won't be read.
      // In that case, first we need to blur it and the we can focus on it again
      // allowing the screen reader to read the new information relative to the error
      if (document.activeElement === element) {
        element.blur();
        // set focus on the next tick so that the browser has time to blur it
        setTimeout(() => element.focus(), 0);
      } else {
        element.focus();
      }
      break; // Exit the loop when an invalid element is found
    }
  }
};

export const useFocusFirstInvalidInput = (
  formRef: MutableRefObject<HTMLFormElement | null> | undefined,
) => {
  const focusFirstInvalid = useCallback(() => {
    if (!formRef?.current) return;
    const firstInvalid = formRef.current.querySelector<HTMLElement>(
      '[aria-invalid="true"]',
    );
    if (firstInvalid) {
      firstInvalid.focus();
    }
  }, [formRef]);

  return focusFirstInvalid;
};

export function FormRenderer<Props>({
  asHtmlForm,
  children,
  formStyle,
  name,
}: {
  asHtmlForm: FormRenderPropsProps<Props>["asHtmlForm"];
  children: ReactNode;
  formStyle: FormRenderPropsProps<Props>["formStyle"];
  name?: string;
}) {
  const formRef = useRef<HTMLFormElement | null>(null);
  const focusFirstInvalid = useFocusFirstInvalidInput(formRef);

  if (asHtmlForm) {
    return (
      <form
        ref={formRef}
        data-testid={`${name}-form`}
        name={name}
        noValidate
        style={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          ...formStyle,
        }}
        onSubmit={(e) => {
          e.preventDefault();
          focusFirstInvalid();
        }}
      >
        {children}
      </form>
    );
  }

  return <>{children}</>;
}

export function FormRenderProps<Props>({
  asHtmlForm,
  children,
  convertIn = (value) => value,
  convertOut = (value) => value,
  formStyle,
  modelDefinition,
  validate = () => true,
  ...props
}: FormRenderPropsProps<Props>) {
  const whitelist = useMemo(
    () =>
      modelDefinition
        ?.filter((m) => m?.show != null)
        .reduce((currentObject: any, newValue: any) => {
          currentObject[newValue.out] = newValue.show(props);
          return currentObject;
        }, {}),
    [modelDefinition],
  );

  return (
    <FormContainerWithTranslations
      {...props}
      convertIn={convertIn}
      convertOut={convertOut}
      validate={validate}
      whitelist={whitelist}
    >
      {({ dirty, onChange, resetValidation, submit, valueChangeObs }) => (
        <StateDispatcherRenderProps
          valueChangeObs={valueChangeObs}
          whitelist={whitelist}
          onChange={onChange}
        >
          {({ onChange: dispatcherOnChange }) => (
            <FormRenderer
              asHtmlForm={asHtmlForm}
              formStyle={formStyle}
              name={props.formName}
            >
              <FormRendererContext.Provider
                value={{
                  dirty,
                  onChange: dispatcherOnChange,
                  resetValidation,
                  submit,
                  valueChangeObs,
                }}
              >
                {children({
                  dirty,
                  onChange: dispatcherOnChange,
                  resetValidation,
                  submit,
                  valueChangeObs,
                })}
              </FormRendererContext.Provider>
            </FormRenderer>
          )}
        </StateDispatcherRenderProps>
      )}
    </FormContainerWithTranslations>
  );
}

export function SimpleFormRenderProps<Props>({
  children,
  formInputValue,
  modelDefinition,
  onSubmit,
  validate,
  ...props
}: SimpleFormRenderPropsProps<Props>) {
  return (
    <FormRenderProps
      convertIn={convertInModel(modelDefinition)}
      convertOut={convertOutModel(modelDefinition)}
      formInputValue={formInputValue}
      modelDefinition={modelDefinition}
      validate={validate ?? validateModel(modelDefinition)}
      onSubmit={onSubmit}
      {...props}
    >
      {children}
    </FormRenderProps>
  );
}

export function withForm<T extends WithFormProps = WithFormProps>(
  WrappedComponent: React.ComponentType<FormRendererContextProps & T>,
) {
  // Try to create a nice displayName for React Dev Tools.
  const displayName =
    WrappedComponent.displayName || WrappedComponent.name || "Component";

  const ComponentWithForm = ({
    convertIn = (value: any) => value,
    convertOut = (value: any) => value,
    formInputValue,
    modelDefinition,
    onSubmit,
    resetAfterSubmit,
    validate = () => true,
    ...props
  }: Omit<T, keyof FormRendererContextProps>) => {
    const whitelist = useMemo(
      () =>
        modelDefinition
          ?.filter((m) => m?.show != null)
          .reduce((currentObject: any, newValue: any) => {
            currentObject[newValue.out] = newValue.show(props);
            return currentObject;
          }, {}),
      [modelDefinition],
    );
    const formProps = {
      convertIn,
      convertOut,
      formInputValue,
      onSubmit,
      resetAfterSubmit,
      validate,
      whitelist,
      ...props,
    };
    return (
      <FormContainerWithTranslations {...formProps}>
        {({ dirty, onChange, resetValidation, submit, valueChangeObs }) => (
          <StateDispatcherRenderProps
            valueChangeObs={valueChangeObs}
            whitelist={whitelist}
            onChange={onChange}
          >
            {({ onChange: dispatcherOnChange }) => (
              <FormRendererContext.Provider
                value={{
                  dirty,
                  onChange: dispatcherOnChange,
                  resetValidation,
                  submit,
                  valueChangeObs,
                }}
              >
                <WrappedComponent
                  {...(props as T)}
                  dirty={dirty}
                  resetValidation={resetValidation}
                  submit={submit}
                  valueChangeObs={valueChangeObs}
                  onChange={dispatcherOnChange}
                />
              </FormRendererContext.Provider>
            )}
          </StateDispatcherRenderProps>
        )}
      </FormContainerWithTranslations>
    );
  };

  ComponentWithForm.displayName = `withForm(${displayName})`;

  return ComponentWithForm;
}

export function Form({
  convertIn = (value: any) => value,
  convertOut = (value: any) => value,
  modelDefinition,
  resetAfterSubmit = false,
  validate = () => true,
}: Omit<WithFormProps, keyof _FormProps> = {}) {
  return function withFormHOC<T extends _FormProps = _FormProps>(
    WrappedComponent: React.ComponentType<FormRendererContextProps & T>,
  ) {
    // Try to create a nice displayName for React Dev Tools.
    const displayName =
      WrappedComponent.displayName || WrappedComponent.name || "Component";

    const ComponentWithForm = ({
      formInputValue,
      onSubmit,
      ...props
    }: Omit<T, keyof FormRendererContextProps>) => {
      const whitelist = useMemo(
        () =>
          modelDefinition
            ?.filter((m) => m?.show != null)
            .reduce((currentObject: any, newValue: any) => {
              currentObject[newValue.out] = newValue.show(props);
              return currentObject;
            }, {}),
        [modelDefinition],
      );
      const formProps = {
        convertIn,
        convertOut,
        formInputValue,
        onSubmit,
        resetAfterSubmit,
        validate,
        whitelist,
        ...props,
      };
      return (
        <FormContainerWithTranslations {...formProps}>
          {({ dirty, onChange, resetValidation, submit, valueChangeObs }) => (
            <StateDispatcherRenderProps
              valueChangeObs={valueChangeObs}
              whitelist={whitelist}
              onChange={onChange}
            >
              {({ onChange: dispatcherOnChange }) => (
                <FormRendererContext.Provider
                  value={{
                    dirty,
                    onChange: dispatcherOnChange,
                    resetValidation,
                    submit,
                    valueChangeObs,
                  }}
                >
                  <WrappedComponent
                    {...(props as T)}
                    dirty={dirty}
                    resetValidation={resetValidation}
                    submit={submit}
                    valueChangeObs={valueChangeObs}
                    onChange={dispatcherOnChange}
                  />
                </FormRendererContext.Provider>
              )}
            </StateDispatcherRenderProps>
          )}
        </FormContainerWithTranslations>
      );
    };

    ComponentWithForm.displayName = `withForm(${displayName})`;

    return ComponentWithForm;
  };
}
